import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useGlobalAction, useUser } from "@gadgetinc/react";
import { api } from "../api";
import { hasAnyRole, isInstitutionAdministrator } from "../lib/roleUtils";

function checkIsAdmin(user) {
  if (!user) return false;
  const userRoles = user?.roles || user?.roleList || [];
  const hasAdminRole = hasAnyRole(userRoles, ["admin", "system-admin", "sysadmin"]);
  return hasAdminRole || isInstitutionAdministrator(user);
}

const pageStyle = {
  minHeight: "100vh",
  background: "#111",
  color: "#fff",
  padding: "1.5rem",
  maxWidth: "1200px",
  margin: "0 auto",
};

const cardStyle = {
  background: "#1f1f1f",
  border: "1px solid #333",
  borderRadius: "12px",
  padding: "1rem",
};

const inputStyle = {
  flex: 1,
  background: "#0f172a",
  border: "1px solid #334155",
  color: "#fff",
  borderRadius: "8px",
  padding: "0.6rem 0.75rem",
};

const buttonStyle = {
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "0.6rem 0.9rem",
  cursor: "pointer",
  fontWeight: 600,
};

export default function AdminScenarioAccessDiagnostic() {
  const user = useUser(api);
  const navigate = useNavigate();
  const isAdmin = checkIsAdmin(user);
  const [email, setEmail] = useState("");
  const [userId, setUserId] = useState("");
  const [repairCohortId, setRepairCohortId] = useState("");
  const [repairRole, setRepairRole] = useState("instructor");
  const [forceScenarioAccess, setForceScenarioAccess] = useState(false);
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [{ fetching }, runDiagnostic] = useGlobalAction(api.diagnoseScenarioGeneratorAccess);
  const [{ fetching: repairing }, runRepair] = useGlobalAction(api.repairLegacyCohortMembership);

  React.useEffect(() => {
    if (user && !isAdmin) navigate("/sign-in");
  }, [user, isAdmin, navigate]);

  const handleRun = async () => {
    setErrorMessage("");
    setResult(null);

    const trimmedEmail = email.trim().toLowerCase();
    const trimmedUserId = userId.trim();

    if (!trimmedEmail && !trimmedUserId) {
      setErrorMessage("Enter a user email or user ID.");
      return;
    }

    try {
      const response = await runDiagnostic({
        ...(trimmedEmail ? { email: trimmedEmail } : {}),
        ...(trimmedUserId ? { userId: trimmedUserId } : {}),
      });
      setResult(response);
    } catch (error) {
      setErrorMessage(error?.message || "Diagnostic failed");
    }
  };

  const handleRepair = async () => {
    setErrorMessage("");

    const trimmedEmail = email.trim().toLowerCase();
    const trimmedUserId = userId.trim();
    const trimmedCohortId = repairCohortId.trim();

    if (!trimmedEmail && !trimmedUserId) {
      setErrorMessage("Enter a user email or user ID before repairing.");
      return;
    }

    if (!trimmedCohortId) {
      setErrorMessage("Enter a cohort ID to repair.");
      return;
    }

    try {
      const repairResult = await runRepair({
        ...(trimmedEmail ? { email: trimmedEmail } : {}),
        ...(trimmedUserId ? { userId: trimmedUserId } : {}),
        cohortId: trimmedCohortId,
        primaryRole: repairRole,
        forceScenarioAccess,
      });
      setResult(repairResult);
    } catch (error) {
      setErrorMessage(error?.message || "Repair failed");
    }
  };

  if (!user) {
    return (
      <div style={{ ...pageStyle, display: "flex", alignItems: "center", justifyContent: "center" }}>
        Loading...
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ ...pageStyle, display: "flex", alignItems: "center", justifyContent: "center" }}>
        Access denied.
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        <h1 style={{ margin: 0, fontSize: "1.6rem" }}>Scenario Access Diagnostic</h1>
        <Link to="/admin" style={{ color: "#93c5fd", textDecoration: "none" }}>
          ← Back to Admin
        </Link>
      </div>

      <p style={{ color: "#d1d5db", marginBottom: "1.25rem" }}>
        Resolve the exact scenario generator access path for a user from your authenticated app session.
      </p>

      <div style={cardStyle}>
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="user email"
            style={inputStyle}
          />
          <input
            type="text"
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
            placeholder="or user ID"
            style={inputStyle}
          />
          <button style={buttonStyle} disabled={fetching} onClick={handleRun}>
            {fetching ? "Running..." : "Run Diagnostic"}
          </button>
        </div>

        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
          <input
            type="text"
            value={repairCohortId}
            onChange={(event) => setRepairCohortId(event.target.value)}
            placeholder="repair cohort ID"
            style={inputStyle}
          />
          <select
            value={repairRole}
            onChange={(event) => setRepairRole(event.target.value)}
            style={{ ...inputStyle, maxWidth: "180px" }}
          >
            <option value="instructor">instructor</option>
            <option value="staff">staff</option>
            <option value="student">student</option>
          </select>
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", color: "#d1d5db" }}>
            <input
              type="checkbox"
              checked={forceScenarioAccess}
              onChange={(event) => setForceScenarioAccess(event.target.checked)}
            />
            force scenario access
          </label>
          <button
            style={{ ...buttonStyle, background: "#047857" }}
            disabled={repairing}
            onClick={handleRepair}
          >
            {repairing ? "Repairing..." : "Repair Legacy Membership"}
          </button>
        </div>

        {errorMessage && (
          <p style={{ color: "#f87171", marginTop: 0 }}>{errorMessage}</p>
        )}

        {result && (
          <pre
            style={{
              margin: 0,
              marginTop: "0.75rem",
              background: "#020617",
              color: "#e2e8f0",
              border: "1px solid #334155",
              borderRadius: "8px",
              padding: "1rem",
              overflow: "auto",
              fontSize: "0.85rem",
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {JSON.stringify(result, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}
